// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { Skin } from '../skin';
import { classicSkin } from './classic';

/**
 * Night Round: dark like classic, but neutral. A blue-black ground instead of green, so one
 * warm amber accent carries the screen and shares a hue with the gold merits. Calm on OLED,
 * easy on the eyes indoors. The hole map keeps classic's fills; they read fine on this ground.
 */
export const nightRoundSkin: Skin = {
  colors: {
    background: {
      app: '#10161F',
      panel: '#1A2230',
      panelAlt: '#1F2837',
      elevated: '#242E3F',
      deep: '#0B1017',
      interactive: '#F2B84B',
      interactiveMuted: '#4A3D1E',
    },
    surface: {
      base: '#1A2230',
      alt: '#1F2837',
      elevated: '#242E3F',
      input: '#151D2A',
      muted: '#2A3446',
      card: '#1C2534',
      light: '#F4F6FA',
      toggleOff: '#3E4B60',
      tint: '#22304A',
      tintMid: '#1B2740',
      tintDeep: '#151F33',
      brown: '#3B2F1A',
      brownLight: '#5A4826',
      brownDeep: '#2A2112',
      brownDark: '#1B1609',
    },
    text: {
      primary: '#EEF2F7',
      secondary: '#B3BDCB',
      muted: '#7D8899',
      heading: '#EEF2F7',
      inverse: '#10161F',
      hint: '#93A0B0',
      subtle: '#C4CDD8',
      faint: '#8794A4',
      dim: '#5E6B7C',
      accentMuted: '#C99A3E',
      accentSoft: '#F5D08A',
      positive: '#BFEBD3',
      warm: '#F6E5C0',
      onLight: '#2A3340',
      slateMuted: '#8FA3B8',
      // Ink that flips with the skin, by role: placeholder text, a label on a strong fill.
      placeholder: '#7D8899',
      onStrong: '#FFFFFF',
      // Ink over photos, video and the map: white in every skin, on purpose.
      onMedia: '#FFFFFF',
    },
    border: {
      default: 'rgba(255,255,255,0.08)',
      strong: '#3E4B60',
      accent: '#F2B84B',
      accentMuted: '#5C4A1F',
      onLight: '#D2D6DC',
      brown: '#8A5A1A',
      // Low-contrast rules on panels; they flip with the skin, unlike a white at low alpha.
      hairline: 'rgba(255,255,255,0.10)',
      subtle: 'rgba(226,232,240,0.30)',
    },
    neutral: {
      white: '#FFFFFF',
      black: '#000000',
      slate: '#E2E8F0',
      navy: '#111827',
      navyDeep: '#0C1435',
      ink: '#040711',
      grey: '#6B7280',
      greyDark: '#5B5B5B',
      charcoal: '#2C2C2C',
    },
    accent: {
      primary: '#F2B84B',
      positive: '#7ED4A2',
      secondary: '#E0A73A',
      light: '#F7CF7A',
      soft: '#F5C766',
      lime: '#D8D25A',
      teal: '#3E8C8A',
      tealDeep: '#1C4B56',
      indigo: '#3B4C7A',
      glow: '#8A6A22',
      subtleBg: '#1F2837',
      subtleBorder: 'rgba(242,184,75,0.3)',
      overlay: 'rgba(16,22,31,0.5)',
      gold: '#F2B84B',
      goldDeep: '#D9A038',
      goldMuted: '#C9A15C',
      goldSoft: 'rgba(242,184,75,0.16)',
      goldBorder: 'rgba(242,184,75,0.45)',
      freeze: '#BFE6FF',
      freezeMuted: 'rgba(191,230,255,0.55)',
      blue: '#6FA8FF',
      blueSoft: '#A9C8FF',
      blueDeep: '#2F5FD1',
      blueMuted: '#3D6FB5',
      mint: '#5FD3A6',
      mintDeep: '#2AA37E',
      mintSoft: '#9BE9CB',
      limeSoft: '#E6F2A5',
      glowBright: '#B58A2E',
      olive: '#D1D27B',
      bronze: '#CD8B52',
      orange: '#F08A2E',
      purple: '#6B5A8F',
    },
    chart: {
      pink: '#F9A8D4',
      peach: '#F0997B',
      salmon: '#E0A18A',
      sand: '#F6D98A',
      sky: '#85B7EB',
    },
    status: {
      danger: '#FF6E66',
      dangerStrong: '#E5484D',
      dangerText: '#FFB3AD',
      dangerTextSoft: '#FFD1CD',
      dangerBg: '#4A1F1D',
      dangerBgMuted: '#3D2322',
      errorText: '#FF8A80',
      errorTextStrong: '#FF7070',
      errorTextSoft: '#FFA8A2',
      warning: '#B8860B',
      warningText: '#F2B84B',
      warningBorder: '#E0A030',
      warningBg: '#3A2C0F',
      success: '#7ED4A2',
      successBg: '#17352A',
      successStrong: '#2FAF6E',
      dangerVivid: '#FF5A52',
      dangerBgStrong: '#5C1F1B',
      dangerPanel: '#2A1210',
      errorTextMuted: '#E58F8F',
      warningStrong: '#E68A00',
      warningBorderMuted: '#8D6232',
    },
    button: {
      primaryBg: '#F2B84B',
      primaryBorder: '#F2B84B',
      primaryText: '#1A1405',
      secondaryBg: '#1F2837',
      secondaryBorder: '#3E4B60',
      secondaryText: '#EEF2F7',
      ghostBg: 'transparent',
      ghostBorder: '#3E4B60',
      ghostText: '#B3BDCB',
    },
  },
  fonts: {
    brand: 'Sora',
    mono: 'SpaceMono',
    webSerif: 'Sora, "Helvetica Neue", Arial, sans-serif',
    systemSerif: 'sans-serif',
    systemMono: 'monospace',
  },
  button: classicSkin.button,
  chrome: classicSkin.chrome,
  map: classicSkin.map,
};
